import upperFirst from 'lodash/upperFirst';
import type { Moment } from 'moment';

import { translate } from '@wecasa/i18n';

import { CLEANING_PRODUCTS, LINEN_OPTION } from '../constants/prestations';
import type { Booking } from '../types/customer';
import { isOrder, isRecurringCleaning } from './booking';
import { FORMATS, formatDate } from './date';
import { formatPrice, formatPricePerHour, formatPricePerSession } from './formatting';
import moment from './moment';

const t = translate('cleaning.recurrence');

type FormatCleaningPriceParams = {
  duration?: number | null;
  isRecurrent?: boolean;
  price?: number;
};

export const formatCleaningPrice = ({ duration, isRecurrent, price }: FormatCleaningPriceParams = {}) => {
  if (!duration) return formatPricePerHour(price);
  if (isRecurrent) return formatPricePerSession(price);
  return formatPrice(price);
};

export const getRecurrenceDescription = (day: Moment, weeklyRecurrence?: number | null) => {
  if (!weeklyRecurrence) return upperFirst(formatDate(day, FORMATS.DATE.WEEKDAY_DAY_MONTH));

  switch (weeklyRecurrence) {
    case 0:
      return upperFirst(formatDate(day, FORMATS.DATE.WEEKDAY_DAY_MONTH));
    case 1:
      return t('.every_days', { day: formatDate(day, FORMATS.DATE.WEEKDAY) });
    case 0.5:
      return t('.once_every_two_weeks', { day: formatDate(day, FORMATS.DATE.WEEKDAY) });
    case 0.25:
      return t('.once_every_four_weeks', { day: formatDate(day, FORMATS.DATE.WEEKDAY) });
    // It's not possible to select multiple recurrences in creation now
    // but we need to keep this code for the existing bookings
    default:
      return t('.several', {
        recurrence: weeklyRecurrence,
      });
  }
};

export const isRecurrentRecurrence = (recurrence?: number | null) => !!recurrence && recurrence > 0;

type IsDateOfOrderRecurrenceParams = {
  booking: Booking;
  selectedDate: Moment;
};

export const isDateOfOrderRecurrence = ({ booking, selectedDate }: IsDateOfOrderRecurrenceParams) =>
  isRecurringCleaning(booking) &&
  isOrder(booking) &&
  !selectedDate.isSame(moment(booking.appointment), 'day') &&
  booking.recurrence_orders?.some((recurrenceOrder) => selectedDate.isSame(moment(recurrenceOrder.appointment), 'day'));

// TODO : DELETE_WHEN_PRO_USE_PRESTATIONS_WITH_OPTIONS_ON_BOOKING
// We keep this function for now as the pro app is still using it.
// As soon as the pro app starts using the booking.prestations_with_options
// instead of the booking.prestations, we can remove it.
export const hasCleaningProductsService = (booking: Booking) => booking.prestations[0].includes(CLEANING_PRODUCTS);
export const hasCleaningLinenOption = (booking: Booking) => booking.prestations[0].includes(LINEN_OPTION);
